import { c as _c } from "react/compiler-runtime";
/**
 * FormFieldSpecial — Special/advanced field components.
 *
 * Handles: storage-value, format-template, code-editor, format-data, delay,
 * for-each (iterable), filter, object (JSON editor), array (simple + object),
 * math-operand, textarea, recurrence (rrule), color, WorkflowPayloadFields (moved
 * to connector since it's integration-related — but keeping JSON array here).
 */
import { Textarea } from "@/components/ui/textarea";
import { formatLabel } from "@/utils/string";
import * as React from "react";
import { useState } from "react";
import { FieldDescription } from "./FieldDescription";
import ArrayInput from "./ArrayInput";
import CheckboxGroupInput from "./CheckboxGroupInput";
import CodeEditorInput from "./CodeEditorInput";
import FidSearchInput from "./FidSearchInput";
import ColorInput from "./ColorInput";
import FilterBuilder from "./FilterBuilder";
import FilterInput from "./FilterInput";
import JsonInput from "./JsonInput";
import JsonSchemaBuilderInput from "./JsonSchemaBuilderInput";
import RecurrenceInput from "./RecurrenceInput";
import SqlEditorInput from "./SqlEditorInput";
import VariableInput from "./VariableInput";
import VariableTextarea from "./VariableTextarea";
import { DatabaseTableWidget } from "./DatabaseTableWidget";
import type { ColumnDef } from "@/app/(v2)/databases/_components/CreateTableModal";
import ArrayTagInput from "./ArrayTagInput";
import { TickerSelect } from "@/components/ui/ticker-select";
import TwitterUserSelector from "./TwitterUserSelector";
import type { FormFieldInternalProps } from "./FormField";

// Known-enrichment fallback map — keeps the enrichmentFilter typed correctly
// when neither the trigger definition nor the node snapshot carries a
// resultSchema. Must stay in sync with the evm-log output schema in
// services/b3os-workflow/configs/json/triggers.json.
const KNOWN_ENRICHMENT_TYPES: Record<string, string> = {
  sender: "string",
  valueUsd: "number"
};

// Separate component for JSON array fields to manage local state
function JsonArrayField(t0) {
  const $ = _c(21);
  const {
    name,
    property,
    value,
    handleChange,
    headerRight
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = Array.isArray(value) ? JSON.stringify(value, null, 2) : "[]";
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialValue = t1;
  const [textValue, setTextValue] = useState(initialValue);
  const [jsonError, setJsonError] = useState(null);
  let t2;
  if ($[2] !== handleChange) {
    t2 = e => {
      const newText = e.target.value;
      setTextValue(newText);
      try {
        const parsed = JSON.parse(newText);
        if (Array.isArray(parsed)) {
          handleChange(parsed);
          setJsonError(null);
        } else {
          setJsonError("Value must be a JSON array");
        }
      } catch {
        setJsonError("Invalid JSON format");
      }
    };
    $[2] = handleChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleTextChange = t2;
  let t3;
  if ($[4] !== headerRight || $[5] !== name || $[6] !== property.title) {
    t3 = (property.title || formatLabel(name)) && <div className="flex items-center justify-between gap-3"><label className="field-label block">{property.title || formatLabel(name)}</label>{headerRight}</div>;
    $[4] = headerRight;
    $[5] = name;
    $[6] = property.title;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== property.description) {
    t4 = property.description && <FieldDescription text={property.description} />;
    $[8] = property.description;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = `w-full rounded-lg font-mono focus:ring-2 ${jsonError ? "border-red-500 focus:border-red-500 focus:ring-red-500/20 dark:border-red-500" : "border-ds-neutral-300 dark:border-ds-neutral-700 focus:border-blue-500 focus:ring-blue-500/20"}`;
  let t6;
  if ($[10] !== handleTextChange || $[11] !== t5 || $[12] !== textValue) {
    t6 = <Textarea value={textValue} onChange={handleTextChange} rows={8} className={t5} placeholder={"[{\"left\": \"{{node.result}}\", \"operator\": \"eq\", \"right\": \"value\"}]"} />;
    $[10] = handleTextChange;
    $[11] = t5;
    $[12] = textValue;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== jsonError) {
    t7 = jsonError ? <p className="micro-label text-red-500 dark:text-red-400">{jsonError}</p> : <p className="label-xs">Enter valid JSON array of objects</p>;
    $[14] = jsonError;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div className="space-y-2">{t3}{t4}{t6}{t7}</div>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}

/**
 * Attempts to render a special/advanced field component.
 * Returns a React element if the field matches, or `null` to indicate
 * the caller should continue to the next category.
 */
export function renderSpecialField({
  name,
  property,
  value,
  handleChange,
  allValues,
  currentNodeId,
  currentNodeType,
  allNodes,
  allEdges,
  hideLabel,
  nodeDefinitions,
  currentNodePayload,
  validationData,
  withVariableToggle
}: FormFieldInternalProps & {
  handleChange: (value: unknown) => void;
  validationData: Record<string, unknown>;
  withVariableToggle: (element: React.ReactElement) => React.ReactElement;
}): React.ReactElement | null {
  // Database table creation widget — used in ASK blocks when Caddie proposes a new table.
  if (property.type === "database-table") {
    return <DatabaseTableWidget label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? undefined : property.description as string | undefined} value={value as string ?? ""} onChange={(v: string) => handleChange(v)} properties={{
      tableName: typeof property.tableName === "string" ? property.tableName : undefined,
      columns: Array.isArray(property.columns) ? (property.columns as unknown[]).filter((c): c is ColumnDef => typeof c === "object" && c !== null && typeof (c as Record<string, unknown>).name === "string" && typeof (c as Record<string, unknown>).type === "string") : undefined
    }} />;
  }

  // x-new-tweet trigger: rich selectors for username and keywords. The schema
  // accepts username as either a single string or an array of usernames
  // (oneOf) — see services/b3os-workflow/configs/json/triggers.json. We store
  // single-user workflows as `string` (wire-format backward compat) and
  // multi-user workflows as `array`.
  if (currentNodeType === "x-new-tweet") {
    if (name === "username") {
      const selectorValue: string | string[] | undefined = Array.isArray(value) ? value : typeof value === "string" && value ? value : undefined;
      return <TwitterUserSelector label={hideLabel ? "" : property.title || formatLabel(name)} value={selectorValue} onChange={v => {
        if (!v || v.length === 0) handleChange("");else if (v.length === 1) handleChange(v[0]);else handleChange(v);
      }} required={property.required} />;
    }
    if (name === "keywords") {
      const label = hideLabel ? "" : property.title || formatLabel(name);
      return <div className="space-y-1">
          {label && <label className="field-label block">{label}</label>}
          {!hideLabel && property.description && <FieldDescription text={property.description} />}
          <ArrayTagInput value={value} onChange={(v: string[]) => handleChange(v)} placeholder="Type keyword and press Enter" caseInsensitiveDedup />
        </div>;
    }
  }

  // Special case for storage action value field - use VariableInput for variable support
  if (property.type === "storage-value" || name === "value" && currentNodeType === "storage") {
    return <VariableInput label={hideLabel ? "" : property.title || "Value"} description={hideLabel ? "" : property.description || "Value to store. Can be a variable like {{node.result.data}} or a literal value"} value={typeof value === "string" ? value : value !== undefined ? JSON.stringify(value) : ""} onChange={(newValue: string) => {
      // Try to parse as JSON for objects/arrays/numbers/booleans
      if (typeof newValue === "string") {
        // Check if it's a variable reference (must be complete {{...}} pattern)
        if (newValue.startsWith("{{") && newValue.endsWith("}}")) {
          handleChange(newValue);
          return;
        }
        // Try to parse as JSON
        try {
          const parsed = JSON.parse(newValue);
          handleChange(parsed);
          return;
        } catch {
          // Keep as string
          handleChange(newValue);
        }
      } else {
        handleChange(newValue);
      }
    }} required={property.required || false} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} placeholder='{{node.result.value}} or "text" or 123 or {"key": "value"}' nodeDefinitions={nodeDefinitions} />;
  }

  // Special case for format action template field - use VariableTextarea for variable support
  if (property.type === "format-template" || name === "template" && currentNodeType === "format") {
    return <VariableTextarea label={hideLabel ? "" : property.title || "Template"} description={hideLabel ? "" : property.description || "Template with {{field}} placeholders. Example: {{name}} is {{age}} years old"} value={value || ""} onChange={handleChange} required={property.required || false} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} maxLength={property.maxLength} />;
  }

  // Special case for code-transform action code field - use CodeEditorInput with syntax highlighting
  if (property.type === "code-editor" || name === "code" && currentNodeType === "code-transform") {
    return <CodeEditorInput name={name} property={property} value={value || ""} onChange={handleChange} allValues={allValues} hideLabel={hideLabel} />;
  }

  // Special case for code-transform input field — accepts variable expressions OR literal objects/arrays.
  // Without this, objects fall through to VariableInput which renders [object Object].
  if (name === "input" && currentNodeType === "code-transform") {
    return <VariableInput label={hideLabel ? "" : property.title || "Input"} description={hideLabel ? "" : property.description || "The data passed to your transform(input) function. Use a variable like {{node.result}} or enter JSON."} value={typeof value === "string" ? value : value != null ? JSON.stringify(value) : ""} onChange={(newValue: string) => {
      const trimmed = newValue.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          handleChange(JSON.parse(trimmed));
          return;
        } catch {
          // Invalid JSON (e.g., user is mid-typing) — save as string
        }
      }
      handleChange(newValue);
    }} required={property.required || false} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} placeholder={'{{node.result}} or {"key": "value"}'} nodeDefinitions={nodeDefinitions} />;
  }

  // Special case for format action data field - use VariableTextarea for variable references
  if (property.type === "format-data" || name === "data" && currentNodeType === "format") {
    return <VariableTextarea label={hideLabel ? "" : property.title || "Data"} description={hideLabel ? "" : property.description || "Array or object to format. Use a variable like {{node.result.items}}"} value={typeof value === "string" ? value : value ? JSON.stringify(value, null, 2) : ""} onChange={(newValue: string) => {
      // Try to parse as JSON, otherwise keep as string (variable reference)
      if (typeof newValue === "string" && (newValue.startsWith("[") || newValue.startsWith("{"))) {
        try {
          const parsed = JSON.parse(newValue);
          handleChange(parsed);
          return;
        } catch {
          // Not valid JSON, keep as string
        }
      }
      handleChange(newValue);
    }} required={property.required || false} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} maxLength={property.maxLength} />;
  }

  // Special case for delay action delayMs field - show human-readable duration
  // Uses VariableInput to support variable suggestion dropdown (e.g. {{root.input.delayMs}})
  if (property.type === "delay" || name === "delayMs" && currentNodeType === "delay") {
    const formatDuration = (ms: number): string => {
      if (ms < 1000) return `${ms}ms`;
      if (ms < 60000) return `${ms / 1000} second${ms !== 1000 ? "s" : ""}`;
      if (ms < 3600000) return `${ms / 60000} minute${ms !== 60000 ? "s" : ""}`;
      return `${ms / 3600000} hour${ms !== 3600000 ? "s" : ""}`;
    };
    const isTemplateValue = typeof value === "string" && value.includes("{{");
    const currentMs = typeof value === "number" ? value : 5000;
    const stringValue = value !== undefined && value !== null ? String(value) : "";
    return <div className="space-y-2">
        <VariableInput label={hideLabel ? "" : property.title || "Delay Duration"} description={hideLabel ? "" : property.description} value={stringValue} onChange={(newValue: string) => {
        // Template expressions stay as strings (resolved at runtime)
        if (newValue.includes("{{")) {
          handleChange(newValue);
          return;
        }
        // Pure numeric strings get converted back to numbers (backend expects float64)
        const num = Number(newValue);
        if (newValue !== "" && !isNaN(num)) {
          handleChange(num);
        } else {
          handleChange(newValue);
        }
      }} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} placeholder="Enter ms or {{ for variables" nodeDefinitions={nodeDefinitions} />
        {!isTemplateValue && typeof value === "number" && <span className="text-sm font-medium text-blue-600 dark:text-blue-400">({formatDuration(currentMs)})</span>}
        <div className="flex flex-wrap gap-2">
          {[1000, 5000, 10000, 30000, 60000].map(preset => <button key={preset} type="button" onClick={() => handleChange(preset)} className={`rounded-md px-2 py-1 text-xs transition-colors ${!isTemplateValue && currentMs === preset ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" : "bg-ds-neutral-100 text-ds-text-subtle hover:bg-ds-neutral-200 dark:bg-ds-neutral-800 dark:text-ds-text-disabled dark:hover:bg-ds-neutral-700"}`}>
              {formatDuration(preset)}
            </button>)}
        </div>
      </div>;
  }

  // Special case for for-each array field - use VariableInput to support both arrays and variable references
  if (property.type === "iterable" || name === "array" && currentNodeType === "for-each") {
    return <VariableInput label={hideLabel ? "" : property.title || "Array"} description={hideLabel ? "" : property.description || "Array to iterate over. Use a variable like {{node.result.items}} or enter JSON array."} value={typeof value === "string" ? value : value ? JSON.stringify(value) : ""} onChange={(newValue: string) => {
      // Try to parse as JSON array, otherwise keep as string (variable reference)
      if (typeof newValue === "string" && newValue.startsWith("[")) {
        try {
          const parsed = JSON.parse(newValue);
          if (Array.isArray(parsed)) {
            handleChange(parsed);
            return;
          }
        } catch {
          // Not valid JSON, keep as string
        }
      }
      handleChange(newValue);
    }} required={property.required || false} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} placeholder="{{node.result.items}} or [1, 2, 3]" nodeDefinitions={nodeDefinitions} />;
  }
  if (name === "enrichmentFilter") {
    const enrichments = allValues.enrichments || currentNodePayload.enrichments;
    if (!Array.isArray(enrichments) || enrichments.length === 0) {
      return null;
    }
    // Derive fields for the FilterBuilder picker from the selected enrichments.
    // Preference order for the JSON-Schema type:
    //   1. Trigger's nodeDefinition.resultSchema (fetched from API)
    //   2. node.data.resultSchema (if injected on canvas nodes)
    //   3. Known-enrichment type map (fallback — stays in sync with
    //      triggers.json output.enrichments.properties).
    // We map JSON-Schema types to the FilterBuilder's ABI-type categories so
    // the operator dropdown matches (numeric enrichments get $gt/$lt,
    // strings get $eq/$in, etc.).
    const triggerDef = nodeDefinitions?.find(d => d.type === currentNodeType) as {
      resultSchema?: Record<string, unknown>;
    } | undefined;
    const currentNode = allNodes.find(n => n.id === currentNodeId);
    const nodeData = currentNode?.data as Record<string, unknown> | undefined;
    const readEnrichmentProps = (schema: Record<string, unknown> | undefined) => {
      const enrichmentSchema = (schema?.properties as Record<string, unknown> | undefined)?.enrichments as Record<string, unknown> | undefined;
      return enrichmentSchema?.properties as Record<string, unknown> | undefined ?? {};
    };
    const enrichmentPropsFromDef = readEnrichmentProps(triggerDef?.resultSchema);
    const enrichmentPropsFromNode = readEnrichmentProps(nodeData?.resultSchema as Record<string, unknown> | undefined);
    const enrichmentFields = (enrichments as string[]).filter(enrichmentName => typeof enrichmentName === "string").map(enrichmentName => {
      const propFromDef = enrichmentPropsFromDef[enrichmentName] as Record<string, unknown> | undefined;
      const propFromNode = enrichmentPropsFromNode[enrichmentName] as Record<string, unknown> | undefined;
      const defType = propFromDef?.type as string | undefined;
      const nodeType = propFromNode?.type as string | undefined;
      if (!defType && !nodeType && !KNOWN_ENRICHMENT_TYPES[enrichmentName]) {
        console.warn(`[FormFieldSpecial] enrichment "${enrichmentName}" has no type resolution; falling back to "string". ` + "Add it to KNOWN_ENRICHMENT_TYPES or ensure trigger resultSchema is loaded.");
      }
      const jsonType = defType || nodeType || KNOWN_ENRICHMENT_TYPES[enrichmentName] || "string";
      const abiType = jsonType === "number" || jsonType === "integer" ? "uint256" : jsonType === "boolean" ? "bool" : "string";
      return {
        name: enrichmentName,
        type: abiType
      };
    });
    return <FilterBuilder label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description} value={typeof value === "string" ? value : JSON.stringify(value, null, 2) ?? ""} onChange={handleChange} fields={enrichmentFields} />;
  }

  // Special case for filter - for EVM-log triggers, hide until chain, contract, and eventAbi are provided
  if (property.type === "filter" || name === "filter") {
    // Only gate on chainId/contractAddress/eventAbi for EVM-log triggers, not for filter action nodes
    if (currentNodeType !== "filter") {
      const currentNode = allNodes.find(n => n.id === currentNodeId);
      const nodeData = currentNode?.data || {};
      const hasChainId = validationData.chainId || currentNodePayload.chainId || allValues.chainId || nodeData.chainId;
      const hasEventAbi = validationData.eventAbi && validationData.eventAbi !== "" || allValues.eventAbi && allValues.eventAbi !== "" || currentNodePayload.eventAbi && currentNodePayload.eventAbi !== "";

      // Hide until chain and event are selected (contractAddress is optional)
      if (!hasChainId || !hasEventAbi) {
        return <></>;
      }
    }

    // For EVM-log triggers, use FilterBuilder with event ABI context
    if (currentNodeType !== "filter") {
      const eventAbiValue = validationData.eventAbi || allValues.eventAbi || currentNodePayload.eventAbi;
      return <FilterBuilder label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description} value={typeof value === "string" ? value : JSON.stringify(value, null, 2) || ""} onChange={handleChange} eventAbi={typeof eventAbiValue === "string" ? eventAbiValue : eventAbiValue ? JSON.stringify(eventAbiValue) : undefined} />;
    }
    return <FilterInput label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description} value={typeof value === "string" ? value : JSON.stringify(value, null, 2) || ""} onChange={handleChange} placeholder={property.placeholder || '{ "$and": [] }'} />;
  }
  if (property.format === "json-schema-builder") {
    return <JsonSchemaBuilderInput label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description} value={value as string | object | undefined ?? "{}"} onChange={handleChange} placeholder="{}" />;
  }

  // Object type (JSON editor)
  // Also handle oneOf schemas that include object type (e.g. format action's `data` field
  // with oneOf: [{type: "array"}, {type: "object"}]) — these have no top-level `type`
  // and would otherwise fall through to default VariableInput (contentEditable),
  // which leaks DOM elements into node state.
  const hasObjectInCompositeSchema = !property.type && ["oneOf", "anyOf", "allOf"].some(key => Array.isArray(property[key]) && (property[key] as Record<string, unknown>[]).some(s => s.type === "object" || s.type === "array"));
  if (property.type === "object" || hasObjectInCompositeSchema) {
    return <JsonInput label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description} value={value ?? "{}"} onChange={handleChange} placeholder="{}" />;
  }

  // Farcaster FID fields — show username search with autocomplete
  const isFarcasterNode = currentNodeType?.startsWith("farcaster-") || currentNodeType?.startsWith("get-farcaster-") || currentNodeType?.startsWith("search-farcaster-") || currentNodeType?.startsWith("follow-farcaster-") || currentNodeType?.startsWith("react-farcaster-") || currentNodeType?.startsWith("delete-farcaster-");

  // Multi-FID array fields (trigger filters: authorFids, targetFids, mentionedFids)
  const isFidArrayField = property.type === "array" && name.toLowerCase().endsWith("fids");
  if (isFarcasterNode && isFidArrayField) {
    const numericValue = (Array.isArray(value) ? value : []).map(Number).filter(n => !isNaN(n) && n > 0);
    return <FidSearchInput multi label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description} value={numericValue} onChange={handleChange} />;
  }

  // Single FID number fields (action inputs: fid, authorFid, targetFid)
  const isSingleFidField = isFarcasterNode && (property.type === "number" || property.type === "integer") && (name === "fid" || name === "authorFid" || name === "targetFid");
  if (isSingleFidField) {
    const numValue = typeof value === "number" ? value : typeof value === "string" && /^\d+$/.test(value) ? Number(value) : undefined;
    return <FidSearchInput label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description} value={numValue} onChange={handleChange} />;
  }
  if (property.type === "array" && property.items?.format === "ticker") {
    return <TickerSelect label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description} value={Array.isArray(value) ? value : []} onChange={handleChange} />;
  }

  // Array type (multiselect or tags) — but NOT slack-channel arrays (handled by connector)
  if (property.type === "array" && property.items?.format !== "slack-channel") {
    // Check if array items are objects (like conditions in AND/OR logic)
    const itemsSchema = (property as Record<string, unknown>).items as Record<string, unknown> | undefined;
    if (itemsSchema?.enum) {
      return <CheckboxGroupInput label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description as string} options={itemsSchema.enum as string[]} optionLabels={itemsSchema.enumNames as string[] | undefined} optionDescriptions={itemsSchema.enumDescriptions as string[] | undefined} value={Array.isArray(value) ? value as string[] : []} onChange={handleChange} />;
    }
    const isObjectArray = itemsSchema?.type === "object";
    if (isObjectArray) {
      return withVariableToggle(<JsonArrayField name={name} property={property} value={value} handleChange={handleChange} />);
    }

    // For simple arrays, use ArrayInput (coerces to number if items.type is "number")
    return <ArrayInput label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description} value={value || []} onChange={handleChange} itemType={itemsSchema?.type as string | undefined} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} />;
  }

  // Special case for math operation fields - use VariableInput for variable support
  if (property.type === "math-operand" || (name === "left" || name === "right") && property.type === "string") {
    return <VariableInput label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description} value={value || ""} onChange={handleChange} required={property.required || false} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} placeholder={`Type a number or {{ for variables`} nodeDefinitions={nodeDefinitions} />;
  }

  // Special case for SQL fields - use SqlEditorInput with syntax highlighting
  if (property.format === "sql" || property.type === "sql") {
    return <SqlEditorInput name={name} property={property} value={value || ""} onChange={handleChange} hideLabel={hideLabel} />;
  }

  // Special case for textarea fields - use VariableTextarea for variable support
  const isStringType = property.type === "string" || Array.isArray(property.type) && property.type.includes("string");
  const isMarkdownTextarea = property.format === "markdown-textarea" && (isStringType || property.type === "textarea");
  if (isMarkdownTextarea || property.type === "textarea" || (property.format === "textarea" || name === "message") && isStringType) {
    return <VariableTextarea label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description} value={value || ""} onChange={handleChange} required={property.required || false} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} maxLength={property.maxLength} showMarkdownToolbar={isMarkdownTextarea} />;
  }

  // Special case for recurrence rules - visual builder with simple/advanced modes
  if (property.type === "rrule" || name.toLowerCase().includes("rrule") && property.type === "string") {
    return <RecurrenceInput label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description || ""} value={value ?? property.default ?? ""} onChange={handleChange} placeholder={property.placeholder} />;
  }

  // Color input with picker - detect by format "color" or field name containing "color"
  const isColorField = property.type === "color" || property.format === "color" || name.toLowerCase().includes("color") && property.type === "string";
  if (isColorField) {
    return <ColorInput label={hideLabel ? "" : property.title || formatLabel(name)} description={hideLabel ? "" : property.description} value={value ?? property.default ?? ""} onChange={handleChange} placeholder={property.placeholder || "#000000 or {{variable}}"} required={property.required || false} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} />;
  }

  // Not a special field — return null to signal "not handled"
  return null;
}